"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowLeft, Loader2, CheckCircle, X } from "lucide-react";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";

interface UnifiedForgotPasswordFormProps {
  /** Path to navigate back to sign-in */
  signInPath?: string;
  /** Path to sign-up page */
  signUpPath?: string;
  /** Show hint about using signup email (not brand contact email) */
  showBrandHint?: boolean;
  /** Override the reset password page URL (defaults to window.location.origin/reset-password) */
  resetPasswordPath?: string;
}

export function UnifiedForgotPasswordForm({
  signInPath = "/signin",
  signUpPath = "/signup",
  showBrandHint = false,
  resetPasswordPath,
}: UnifiedForgotPasswordFormProps) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const requestReset = useMutation(api.authActions.requestPasswordReset);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const baseUrl = window.location.origin;
      const redirectTo = resetPasswordPath
        ? `${baseUrl}${resetPasswordPath}`
        : `${baseUrl}/reset-password`;

      const result = await requestReset({
        email: email.toLowerCase(),
        redirectTo,
      });

      if (result && result.success) {
        setSuccess(true);
      } else {
        setError(
          result?.error ||
            "Failed to send reset email. Please try again."
        );
        setSuccess(false);
      }
    } catch (err: any) {
      console.error("Forgot password error:", err);
      setError(
        err.message || "Failed to send reset email. Please try again."
      );
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="p-8 text-center">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
          style={{ backgroundColor: "rgba(16, 185, 129, 0.2)" }}
        >
          <CheckCircle className="h-8 w-8 text-emerald-400" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">
          Check your email
        </h1>
        <p className="text-gray-400 mb-6">
          We&apos;ve sent a password reset link to{" "}
          <span className="text-white font-medium">{email}</span>
        </p>
        <p className="text-sm text-gray-500 mb-6">
          Didn&apos;t receive the email? Check your spam folder or try
          again.
        </p>
        <button
          onClick={() => {
            setSuccess(false);
            setEmail("");
          }}
          className="w-full py-3 px-4 rounded-lg text-gray-300 hover:text-white transition-colors"
          style={{
            backgroundColor: "#252542",
            border: "1px solid #2d2d44",
          }}
        >
          Try another email
        </button>
        <Link
          href={signInPath}
          className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 mt-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Sign In
        </Link>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Back Link */}
      <Link
        href={signInPath}
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors group"
      >
        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
        <span>Back to Sign In</span>
      </Link>

      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <Image
            src="/images/kuttoslogo.png"
            alt="Kuttos"
            width={64}
            height={64}
            className="rounded-lg"
          />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">
          Forgot password?
        </h1>
        <p className="text-gray-400 text-sm">
          Enter your email and we&apos;ll send you a reset link
        </p>
        {showBrandHint && (
          <p className="text-xs mt-2 text-gray-500">
            Use the email address you signed up with, not your
            brand&apos;s contact email.
          </p>
        )}
      </div>

      {/* Error */}
      {error && (
        <div
          className="mb-6 p-4 rounded-lg flex items-start gap-3"
          style={{
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.2)",
          }}
        >
          <X className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError("");
            }}
            placeholder="Enter your email"
            required
            autoComplete="email"
            autoFocus
            className="w-full px-4 py-3 rounded-lg placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors text-white"
            style={{
              backgroundColor: "#252542",
              border: "1px solid #2d2d44",
            }}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Sending...
            </>
          ) : (
            "Send reset link"
          )}
        </button>
      </form>

      {/* Sign Up Link */}
      {signUpPath && (
        <p className="text-center text-gray-400 mt-6 text-sm">
          Don&apos;t have an account?{" "}
          <Link
            href={signUpPath}
            className="text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            Sign up
          </Link>
        </p>
      )}
    </div>
  );
}
